<template>
  <div>
    <div class="flex items-center justify-between mb-4">
      <h3 class="text-sm font-medium text-gray-900 dark:text-gray-100">
        Quality Profile & Benchmarks
      </h3>
      <button @click="$emit('back')" class="btn btn-secondary btn-sm">
        Back to Results
      </button>
    </div>

    <div
      v-if="benchmarkConfidence"
      :class="benchmarkConfidence.approval_allowed ? 'border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20' : 'border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20'"
      class="mb-4 rounded-lg border p-3">
      <div class="flex flex-wrap items-center gap-2 mb-2">
        <span class="text-xs font-medium text-gray-900 dark:text-gray-100">
          Benchmark confidence: {{ benchmarkConfidence.confidence_label }} ({{ benchmarkConfidence.confidence_score }}%)
        </span>
        <span class="text-xs text-gray-600 dark:text-gray-300">
          {{ benchmarkConfidence.analyzed_count }}/{{ benchmarkConfidence.selected_count }} analyzed · minimum {{ benchmarkConfidence.minimum_required }}
        </span>
      </div>
      <p v-for="warning in benchmarkConfidence.warnings || []" :key="warning" class="text-xs text-amber-800 dark:text-amber-300">
        {{ warning }}
      </p>
    </div>

    <div class="mb-4 rounded-lg border border-gray-200 dark:border-gray-700 p-3">
      <h4 class="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
        Tier impact on benchmarks
      </h4>
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-gray-500 dark:text-gray-400">
        <div v-for="(note, tier) in tierNotes" :key="tier">
          <span class="font-medium capitalize text-gray-700 dark:text-gray-300">{{ tier }}:</span> {{ note }}
        </div>
      </div>
    </div>

    <p v-if="approvalError" class="mb-4 text-sm text-red-600 dark:text-red-400">
      {{ approvalError }}
    </p>

    <!-- Competitor Quality Cards -->
    <div class="space-y-3 mb-6">
      <div v-for="comp in competitors" :key="comp.id" class="rounded-xl border border-gray-200 dark:border-gray-700 p-4">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-3">
            <span :class="gradeBadge(comp.overall_quality_grade)" class="px-2.5 py-1 rounded text-sm font-bold">
              {{ comp.overall_quality_grade }}
            </span>
            <div>
              <div class="text-sm font-medium">
                #{{ comp.serp_position }} {{ comp.domain }}
              </div>
              <div class="flex flex-wrap gap-2 mt-1">
                <span v-if="comp.competitor_type_label" class="px-1.5 py-0.5 rounded-lg text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                  {{ comp.competitor_type_label }}
                </span>
                <span v-if="comp.location_match_label && comp.competitor_type === 'direct_competitor'" class="px-1.5 py-0.5 rounded-lg text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                  {{ comp.location_match_label }}
                </span>
                <span v-if="comp.result_page_type_label" class="px-1.5 py-0.5 rounded-lg text-xs bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300">
                  {{ comp.result_page_type_label }}
                </span>
                <span v-for="(grade, dim) in comp.quality_grades || {}" :key="dim" :class="gradeBadge(grade)" class="px-1.5 py-0.5 rounded-lg text-xs">
                  {{ dimensionLabel(dim) }}: {{ grade }}
                </span>
              </div>
            </div>
          </div>
          <select
            v-model="tierAdjustments[comp.id]"
            :aria-label="`Benchmark tier for ${comp.domain}`"
            class="rounded-lg border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white text-sm">
            <option value="elevated">
              Elevated
            </option>
            <option value="standard">
              Standard
            </option>
            <option value="deprioritized">
              Deprioritized
            </option>
            <option value="excluded">
              Excluded
            </option>
          </select>
        </div>
        <div class="mt-3 rounded-lg bg-gray-50 dark:bg-gray-900/50 px-3 py-2">
          <div class="text-[11px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Why this tier?
          </div>
          <p class="text-xs text-gray-700 dark:text-gray-300 mt-0.5">
            <template v-if="tierAdjustments[comp.id] !== (comp.benchmark_tier || 'standard')">
              Will be set manually to {{ tierAdjustments[comp.id] }} when you approve.
            </template>
            <template v-else>
              {{ comp.tier_reason || 'No reason recorded for this tier yet.' }}
            </template>
          </p>
          <p v-if="comp.tier_source" class="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
            Source: {{ tierSourceLabel(comp.tier_source) }}
          </p>
        </div>
      </div>
    </div>

    <!-- Benchmarks Table -->
    <div v-if="benchmarks.length" class="mb-6">
      <h4 class="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">
        Raw vs Cleaned Benchmarks
      </h4>
      <div class="overflow-x-auto">
        <table class="min-w-full text-sm">
          <thead>
            <tr class="border-b border-gray-200 dark:border-gray-700">
              <th class="text-left py-2 pr-4 text-xs text-gray-500 uppercase">
                Metric
              </th>
              <th class="text-right py-2 px-4 text-xs text-gray-500 uppercase">
                Raw
              </th>
              <th class="text-right py-2 px-4 text-xs text-gray-500 uppercase">
                Cleaned
              </th>
              <th class="text-right py-2 pl-4 text-xs text-gray-500 uppercase">
                Inflation
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="bm in benchmarks" :key="bm.metric_name" class="border-b border-gray-100 dark:border-gray-800">
              <td class="py-2 pr-4 text-gray-700 dark:text-gray-300 capitalize">
                {{ metricLabel(bm.metric_name) }}
              </td>
              <td class="py-2 px-4 text-right text-gray-500">
                {{ formatNum(bm.raw_value) }}
              </td>
              <td class="py-2 px-4 text-right font-medium text-gray-900 dark:text-gray-100">
                {{ formatNum(bm.cleaned_value) }}
              </td>
              <td class="py-2 pl-4 text-right" :class="bm.inflation_percentage > 0 ? 'text-amber-600' : 'text-green-600'">
                {{ bm.inflation_percentage > 0 ? '+' : '' }}{{ bm.inflation_percentage }}%
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Approve Button -->
    <div class="flex justify-end">
      <button
        @click="handleApprove"
        :disabled="approving || !canApprove"
        class="inline-flex items-center px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium text-sm transition-colors disabled:opacity-50">
        {{ approving ? 'Saving...' : 'Approve Quality Profile' }}
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed } from 'vue'
import { approveQualityProfile } from '~/javascript/api/competitor_analysis.js'

const props = defineProps({
  competitors: { type: Array, required: true },
  benchmarks: { type: Array, required: true },
  serpAnalysis: { type: Object, required: true },
  pageId: { type: String, required: true }
})

const emit = defineEmits(['approved', 'back'])

const approving = ref(false)
const approvalError = ref('')
const benchmarkConfidence = computed(() => props.serpAnalysis?.benchmark_confidence || null)
const canApprove = computed(() => benchmarkConfidence.value?.approval_allowed === true)
const tierNotes = computed(() => benchmarkConfidence.value?.tier_notes || {
  elevated: 'Included with extra weight.',
  standard: 'Included at normal weight.',
  deprioritized: 'Included with reduced weight.',
  excluded: 'Not included in cleaned benchmarks.'
})

const tierAdjustments = reactive({})
props.competitors.forEach((c) => {
  tierAdjustments[c.id] = c.benchmark_tier || 'standard'
})

const gradeBadge = (grade) => {
  const map = {
    A: 'bg-green-100 dark:bg-green-900/30 text-green-700',
    B: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700',
    C: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700',
    D: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700',
    F: 'bg-red-100 dark:bg-red-900/30 text-red-700'
  }
  return map[grade] || map.F
}

const DIMENSION_LABELS = {
  headings: 'SEO relevance (headings)',
  heading_structure: 'Heading structure',
  eeat: 'E-E-A-T'
}
const dimensionLabel = (dim) => DIMENSION_LABELS[dim] || dim

const METRIC_LABELS = {
  contextual_internal_link_count: 'Contextual internal links',
  internal_link_count: 'Internal links (all, incl. nav/footer)',
  external_link_count: 'External links',
  seo_heading_pct: 'Heading SEO relevance score',
  eeat_score: 'E-E-A-T score'
}
const metricLabel = (name) => METRIC_LABELS[name] || name.replace(/_/g, ' ')

const TIER_SOURCE_LABELS = {
  selection: 'SERP selection',
  grading: 'Quality grading',
  manual: 'Set manually',
  over_optimization: 'Over-optimization review'
}
const tierSourceLabel = (source) => TIER_SOURCE_LABELS[source] || source

const formatNum = (val) => {
  if (val == null) return '-'
  return Number(val).toFixed(1)
}

const handleApprove = async() => {
  approving.value = true
  approvalError.value = ''
  try {
    await approveQualityProfile(props.pageId, tierAdjustments)
    emit('approved')
  } catch (e) {
    approvalError.value = e.response?.data?.message || 'Unable to approve the quality profile.'
    // eslint-disable-next-line no-console
    console.error('Error approving:', e)
  } finally {
    approving.value = false
  }
}
</script>
